import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, Star, CheckCircle } from "lucide-react";

const PATHWAY = [
  {
    phase: "Foundation Year (FPY)",
    duration: "Year 1",
    points: [
      "Complete the 52-week foundation pharmacist year with a designated supervisor",
      "Work towards the Foundation Pharmacist Credential (mandatory from 2026)",
      "Undertake a hospital rotation if not already in secondary care",
      "Begin identifying a specialist area of interest — rheumatology is an excellent choice given DMARD complexity",
    ],
  },
  {
    phase: "Early Specialist Development (Band 6–7)",
    duration: "Years 1–3",
    points: [
      "Seek a rheumatology rotation or a Band 6/7 post in a rheumatology-active hospital pharmacy",
      "Begin a relevant postgraduate qualification — PGCert Clinical Pharmacy or MSc Pharmacy Practice",
      "Attend BSR, RPS, and CPPE rheumatology-related events and training",
      "Develop competence in DMARD monitoring, biologics, biosimilars, and patient counselling",
      "Register for independent prescribing training if not already completed",
    ],
  },
  {
    phase: "Consolidating Specialist Practice (Band 7–8a)",
    duration: "Years 3–6",
    points: [
      "Practise as an NMP within your rheumatology scope of practice",
      "Contribute to rheumatology MDT meetings and clinics independently",
      "Lead audit, quality improvement, and service development projects",
      "Begin building your RPS Advanced Credentialing portfolio (APPF four pillars)",
      "Take on teaching and supervision of junior pharmacists and students",
      "Publish audit findings, case reports, or service evaluations where possible",
    ],
  },
  {
    phase: "Advanced Practice (Band 8a+)",
    duration: "Years 5+",
    points: [
      "Achieve RPS Advanced Pharmacist Credentialing",
      "Lead the rheumatology pharmacy service — clinic development, formulary governance, BLUETEQ/NICE TA adherence",
      "Represent pharmacy at divisional or trust level governance meetings",
      "Mentor junior pharmacists and contribute to postgraduate education",
      "Consider whether the Consultant Pharmacist (Band 8c/8d) or academic route is your goal",
    ],
  },
];

const QUALIFICATIONS = [
  { name: "PGCert Clinical Pharmacy", when: "Year 1–2", org: "Various universities" },
  { name: "PGDip Clinical Pharmacy", when: "Year 2–3", org: "Various universities" },
  { name: "MSc Pharmacy Practice / Clinical Pharmacy", when: "Year 2–4", org: "Various universities" },
  { name: "Independent Prescribing", when: "Year 1–3 (once eligible)", org: "GPhC-approved providers" },
  { name: "RPS Foundation Credential", when: "Foundation year", org: "Royal Pharmaceutical Society" },
  { name: "RPS Advanced Credential", when: "Band 7–8a", org: "Royal Pharmaceutical Society" },
  { name: "PhD (optional, academic route)", when: "Any stage", org: "University (usually funded)" },
];

export default function FoundationToSpecialist() {
  return (
    <PharmacistLayout title="Foundation to Specialist">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Star className="h-7 w-7 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">Foundation to Specialist Pharmacist</h2>
              <p className="text-emerald-300 text-sm">Rheumatology Pharmacy Career Pathway</p>
            </div>
          </div>
          <p className="text-emerald-100 text-sm leading-relaxed">
            A structured guide to building a career as a UK rheumatology specialist pharmacist — from your foundation year through to advanced practice. The path is not linear; adjust the timeline to your circumstances and opportunities.
          </p>
        </div>

        <div className="space-y-4">
          {PATHWAY.map((phase, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-700 font-bold text-sm">{i + 1}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-slate-900">{phase.phase}</h3>
                    <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full font-medium shrink-0">{phase.duration}</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {phase.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-bold text-slate-900 mb-4">Key Qualifications Timeline</h4>
          <div className="space-y-3">
            {QUALIFICATIONS.map((q, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="shrink-0 bg-violet-100 text-violet-700 text-xs font-medium px-2.5 py-1 rounded-lg mt-0.5">{q.when}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{q.name}</p>
                  <p className="text-xs text-slate-500">{q.org}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h4 className="font-bold text-emerald-900 mb-2">Building Your Specialist CV</h4>
          <p className="text-sm text-slate-700 leading-relaxed">
            Rheumatology employers value: <strong>NMP qualification</strong>, <strong>audit and QI experience</strong>, <strong>MDT participation</strong>, BSR/RPS conference attendance, and evidence of <strong>patient education</strong> work. Build a portfolio of case studies, audit reports, and letters of commendation from supervising consultants and specialist nurses.
          </p>
        </div>
      </div>
    </PharmacistLayout>
  );
}
